import React, { useState, useEffect } from 'react';
import {
  MembershipCategory,
  ApplicationType,
  Gender,
  MaritalStatus,
  RegistrationFormData,
  AcademicEntry,
  EmploymentEntry,
  FormConfig
} from './types';
import { WIZARD_STEPS, MONTHS } from './constants';
import {
  FormInput,
  FormTextArea,
  FormSelect,
  SectionTitle,
  Card,
  FileUpload
} from './components/UIElements';
import PaymentStep from './components/PaymentStep';
import { defaultContent } from './defaultContent';
import { defaultTheme } from './defaultTheme';

const INITIAL_DATA: RegistrationFormData = {
  membershipCategory: '',
  applicationType: '',
  existingMembershipId: '',
  surname: '',
  firstName: '',
  otherNames: '',
  dob: '',
  sex: '',
  maritalStatus: '',
  passportPhoto: null,
  officeAddress: '',
  officeTelephone: '',
  officeEmail: '',
  homeAddress: '',
  postalAddress: '',
  mobileTelephone: '',
  homeEmail: '',
  degrees: [{ id: '1', name: '', institution: '', yearGraduated: '', certificate: null }],
  diplomas: [],
  studentCourse: '',
  studentInstitution: '',
  studentCommenced: '',
  studentCompletion: '',
  studentMode: '',
  hodCertificate: null,
  hodSignature: null,
  employmentHistory: '',
  professionalBodies: '',
  careerSummary: '',
  referee1: { name: '', address: '', signature: null, date: '', membershipId: '' },
  referee2: { name: '', address: '', signature: null, date: '', membershipId: '' },
  chapterName: '',
  chapterDate: '',
  chapterChairman: '',
  otherInfo: '',
  amountEnclosed: '',
  declarationConfirmed: false,
  applicantSignature: null,
  submissionDate: new Date().toISOString().split('T')[0],
  paymentMethod: '',
  paymentReference: '',
  paymentReceipt: null,
  paymentStatus: '',
  fellowCitationUpload: null,
  fellowStatementUpload: null,
};

const RegistrationWizard: React.FC = () => {
  const settings = (window as any).nifst_settings || {
    root: '/wp-json/nifst/v1/',
    nonce: '',
    success_url: '/thank-you',
    is_admin: false,
    user_data: null,
    target_user_id: 0,
    form_config: {} as FormConfig // Default empty
  };

  const formConfig: FormConfig = settings.form_config || {};

  // Merge Configs
  const content = { ...defaultContent, ...(formConfig.content || {}) };
  // Deep merge steps and labels if needed, but simple spread is a start. 
  // Ideally, use a deep merge utility, but for now we assume full objects or careful editing.
  // Let's do a slightly better merge for steps/labels to avoid wiping defaults if partial config provided.
  if (formConfig.content) {
    if (formConfig.content.steps) content.steps = { ...defaultContent.steps, ...formConfig.content.steps };
    if (formConfig.content.labels) content.labels = { ...defaultContent.labels, ...formConfig.content.labels };
    if (formConfig.content.header) content.header = { ...defaultContent.header, ...formConfig.content.header };
  }

  const theme = { ...defaultTheme, ...(formConfig.theme || {}) };

  const showEmployment = formConfig.enable_employment !== '0';
  const showReferees = formConfig.enable_referees !== '0';
  const declarationText = formConfig.declaration_text || content.declaration;

  // Apply Theme
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--primary-color', theme.primaryColor);
    root.style.setProperty('--secondary-color', theme.secondaryColor);
    root.style.setProperty('--bg-color', theme.backgroundColor);
    root.style.setProperty('--font-family', theme.fontFamily);
    root.style.setProperty('--border-radius', theme.borderRadius);
  }, [theme]);

  const isAdmin = settings.is_admin;

  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState<RegistrationFormData>(INITIAL_DATA);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const getActiveSteps = () => {
    if (formData.membershipCategory === MembershipCategory.FELLOW) {
      return isAdmin ? [1, 13] : [1, 13, 12];
    }
    const steps = [1, 2, 3, 4, 5];
    if (showEmployment) steps.push(6);
    steps.push(7);
    steps.push(8);
    if (showReferees) steps.push(9);
    steps.push(10);
    steps.push(11);
    if (!isAdmin) steps.push(12); // Payment step only for frontend users
    return steps;
  };

  const activeSteps = getActiveSteps();

  useEffect(() => {
    if (isAdmin && settings.user_data) {
      setFormData(prev => ({ ...prev, ...settings.user_data }));
    } else {
      const saved = localStorage.getItem('nifst_form_draft');
      if (saved) {
        try { setFormData(JSON.parse(saved)); } catch (e) { console.error(e); }
      } else if (settings.is_logged_in) {
        setFormData(prev => ({
          ...prev,
          officeEmail: settings.user_email || '',
          surname: settings.user_last_name || '',
          firstName: settings.user_first_name || '',
          otherNames: '',
        }));
      }
    }
  }, []);

  useEffect(() => {
    if (!isAdmin) {
      localStorage.setItem('nifst_form_draft', JSON.stringify(formData));
    }
  }, [formData]);

  const validateEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.toLowerCase());

  const updateFormData = (updates: Partial<RegistrationFormData>) => {
    setFormData(prev => ({ ...prev, ...updates }));
  };

  const validateStep = (currentStep: number): Record<string, string> => {
    const stepErrors: Record<string, string> = {};
    const today = new Date().toISOString().split('T')[0];

    // Validation logic uses hardcoded messages. Ideally these should be configurable too, but mostly standard.
    // Keeping hardcoded for now as per plan to focus on visible labels.

    if (currentStep === 1) {
      if (!formData.membershipCategory) stepErrors.membershipCategory = "Membership category is required";
      if (!formData.applicationType) stepErrors.applicationType = "Application type is required";
    }

    if (currentStep === 2) {
      // Membership ID required for Fellow upgrade
      if (formData.applicationType === ApplicationType.UPGRADING && formData.membershipCategory === MembershipCategory.FELLOW) {
        if (!formData.existingMembershipId.trim()) stepErrors.existingMembershipId = 'Membership ID is required for Fellow upgrade';
      }
      if (!formData.surname.trim()) stepErrors.surname = `${content.labels.surname} is required`;
      if (!formData.firstName.trim()) stepErrors.firstName = `${content.labels.firstName || 'First Name'} is required`;
      if (!formData.otherNames.trim()) stepErrors.otherNames = `${content.labels.otherNames} is required`;
      if (!formData.dob) stepErrors.dob = `${content.labels.dob} is required`;
      if (!formData.sex) stepErrors.sex = `${content.labels.sex} is required`;
      if (!formData.maritalStatus) stepErrors.maritalStatus = `${content.labels.maritalStatus} is required`;
      if (!formData.passportPhoto) stepErrors.passportPhoto = `${content.labels.passportPhoto} is required`;
    }

    if (currentStep === 3) {
      if (!formData.officeEmail.trim()) stepErrors.officeEmail = `${content.labels.officeEmail} is required`;
      else if (!validateEmail(formData.officeEmail)) stepErrors.officeEmail = "Invalid email format";
      if (!formData.homeAddress.trim()) stepErrors.homeAddress = `${content.labels.homeAddress} is required`;
      if (!formData.homeEmail.trim()) stepErrors.homeEmail = `${content.labels.homeEmail} is required`;
      else if (!validateEmail(formData.homeEmail)) stepErrors.homeEmail = "Invalid email format";
    }

    if (currentStep === 11) {
      if (!formData.declarationConfirmed) stepErrors.declarationConfirmed = "You must confirm the declaration";
      if (!formData.applicantSignature) stepErrors.applicantSignature = `${content.labels.applicantSignature} is required`;
    }

    if (currentStep === 9) {
      if (!formData.referee1.name.trim()) stepErrors['referee1.name'] = 'Referee 1 name is required';
      if (!formData.referee1.membershipId.trim()) stepErrors['referee1.membershipId'] = 'Referee 1 membership ID is required';
      if (!formData.referee2.name.trim()) stepErrors['referee2.name'] = 'Referee 2 name is required';
      if (!formData.referee2.membershipId.trim()) stepErrors['referee2.membershipId'] = 'Referee 2 membership ID is required';
    }

    if (currentStep === 13) {
      if (!formData.fellowCitationUpload) stepErrors.fellowCitationUpload = "Citation upload is required.";
      if (!formData.fellowStatementUpload) stepErrors.fellowStatementUpload = "Statement of Activities upload is required.";
    }

    return stepErrors;
  };

  const handleNext = () => {
    if (step === 1 && formData.membershipCategory === MembershipCategory.FELLOW && !settings.is_logged_in && !isAdmin) {
      const loginUrl = settings.login_url || 'https://nifst.org/tslogin/';
      const currentPath = window.location.pathname;
      
      if (currentPath.includes('tslogin')) {
        alert('You must log in to your existing account using the login form on this page to apply for Fellow status.');
        return;
      }

      const relativeRedirect = currentPath + window.location.search;
      window.location.href = `${loginUrl}?redirect_to=${encodeURIComponent(relativeRedirect)}`;
      return;
    }

    const stepErrors = validateStep(step);
    if (Object.keys(stepErrors).length > 0) {
      setErrors(stepErrors);
      return;
    }
    setErrors({});
    const currentIndex = activeSteps.indexOf(step);
    if (currentIndex < activeSteps.length - 1) {
      setStep(activeSteps[currentIndex + 1]);
    }
    window.scrollTo(0, 0);
  };

  const handleBack = () => {
    const currentIndex = activeSteps.indexOf(step);
    if (currentIndex > 0) {
      setStep(activeSteps[currentIndex - 1]);
    }
    window.scrollTo(0, 0);
  };

  const doSubmit = async (dataToSubmit: RegistrationFormData) => {
    setIsSubmitting(true);
    try {
      const endpoint = isAdmin ? 'admin-update-registration' : 'submit-registration';
      const payload = isAdmin ? { target_user_id: settings.target_user_id, formData: dataToSubmit } : dataToSubmit;

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      if (isAdmin || settings.is_logged_in) {
        headers['X-WP-Nonce'] = settings.nonce;
      }

      const response = await fetch(settings.root + endpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        let errorMsg = `Server error (${response.status}). The submission may be too large or the server is unavailable.`;
        try {
          // Try to parse WP_Error JSON response
          const errorJson = await response.json();
          if (errorJson && errorJson.message) {
            errorMsg = errorJson.message;
          }
        } catch (e) {
          // Fallback to text if not JSON
          const errorText = await response.text().catch(() => '');
          if (errorText && errorText.length < 200) {
            // Only use text if it's reasonably short, otherwise keep generic message
            errorMsg = `Server error (${response.status}): ${errorText}`;
          }
        }
        throw new Error(errorMsg);
      }

      const result = await response.json();

      if (result.success) {
        if (isAdmin) {
          alert('User data updated successfully.');
          setIsSubmitted(false);
        } else {
          setIsSubmitted(true);
          localStorage.removeItem('nifst_form_draft');
          if (result.redirect_url) window.location.href = result.redirect_url;
        }
      } else {
        alert(result.message || 'Submission failed. Please try again.');
      }
    } catch (error: any) {
      console.error('Submission Error:', error);
      alert('Submission failed: ' + (error?.message || 'An unexpected error occurred. Please try again.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isAdmin) {
      const finalErrors = validateStep(step);
      if (Object.keys(finalErrors).length > 0) {
        setErrors(finalErrors);
        return;
      }
      await doSubmit(formData);
    }
    // For frontend users, step 11/13 validates and moves to payment step 12
  };

  const handlePaymentComplete = async (paymentData: {
    method: 'online' | 'offline';
    reference: string;
    receipt: string | null;
    status: 'completed' | 'pending_verification';
  }) => {
    try {
      const updatedData: RegistrationFormData = {
        ...formData,
        paymentMethod: paymentData.method,
        paymentReference: paymentData.reference,
        paymentReceipt: paymentData.receipt,
        paymentStatus: paymentData.status,
      };
      setFormData(updatedData);
      await doSubmit(updatedData);
    } catch (error: any) {
      console.error('Payment completion error:', error);
      alert('An error occurred while submitting. Please try again.');
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center py-12">
          <div className="w-20 h-20 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-6">
            <i className="fa-solid fa-check text-4xl"></i>
          </div>
          <h2 className="text-2xl font-bold text-slate-800 mb-2">Application received!</h2>
          <p className="text-slate-600 mb-8">
            Your membership application for NIFST successful and has been recorded. You will be contacted shortly.
          </p>
        </Card>
      </div>
    );
  }

  // NOTE: We don't render the header here anymore as per previous request.

  return (
    <div className="min-h-screen flex flex-col" style={{ fontFamily: 'var(--font-family)' }}>
      <main className="flex-1 max-w-4xl w-full mx-auto px-4 py-8">
        {theme.logoUrl && (
          <div className="flex justify-center mb-8">
            <img src={theme.logoUrl} alt="Logo" className="max-h-24 w-auto object-contain" />
          </div>
        )}
        <div className="mb-10 overflow-x-auto">
          <div className="flex justify-between items-center min-w-[800px] px-2">
            {content.wizardSteps.map((label, i) => {
              const stepNum = i + 1;
              if (!activeSteps.includes(stepNum)) return null;

              return (
                <div key={label} className="flex flex-col items-center gap-2 group flex-1">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-all border-2 ${step > stepNum ? 'bg-[var(--primary-color)] border-[var(--primary-color)] text-white' :
                    step === stepNum ? 'bg-white border-[var(--primary-color)] text-[var(--primary-color)] scale-110 shadow-md' :
                      'bg-white border-slate-200 text-slate-300'
                    }`}>
                    {step > stepNum ? <i className="fa-solid fa-check"></i> : stepNum}
                  </div>
                  <span className={`text-[10px] font-bold uppercase tracking-wider ${step === stepNum ? 'text-[var(--secondary-color)]' : 'text-slate-400'
                    }`}>
                    {label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        <Card className="mb-8" style={{ borderRadius: 'var(--border-radius)' }}>
          <form onSubmit={handleSubmit} className="space-y-8">
            {step === 1 && (
              <div className="space-y-8">
                <div>
                  <SectionTitle subtitle={content.steps.appType.subtitle}>{content.steps.appType.title}</SectionTitle>
                  <div className="flex gap-6">
                    {Object.values(ApplicationType).map(type => (
                      <label key={type} className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="applicationType"
                          className="w-5 h-5 text-[var(--primary-color)]"
                          checked={formData.applicationType === type}
                          onChange={() => updateFormData({ applicationType: type, membershipCategory: '' })}
                        />
                        <span className="ml-2 text-slate-700">{type}</span>
                      </label>
                    ))}
                  </div>
                  {errors.applicationType && <p className="text-red-500 text-xs mt-2 font-medium">{errors.applicationType}</p>}
                </div>
                {formData.applicationType && (
                  <div>
                    <SectionTitle subtitle={content.steps.category.subtitle}>{content.steps.category.title}</SectionTitle>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {(formData.applicationType === ApplicationType.NEW
                        ? [MembershipCategory.PROFESSIONAL, MembershipCategory.ASSOCIATE, MembershipCategory.LICENTIATE, MembershipCategory.STUDENT]
                        : [MembershipCategory.FELLOW, MembershipCategory.PROFESSIONAL, MembershipCategory.ASSOCIATE, MembershipCategory.LICENTIATE]
                      ).map(cat => (
                        <label key={cat} className={`flex items-center p-4 border rounded-xl cursor-pointer transition-all ${formData.membershipCategory === cat ? 'border-[var(--primary-color)] bg-emerald-50 ring-1 ring-[var(--primary-color)]' : 'border-slate-200 hover:border-emerald-200'
                          }`}>
                          <input
                            type="radio"
                            className="w-5 h-5 text-[var(--primary-color)] focus:ring-[var(--primary-color)]"
                            name="membershipCategory"
                            checked={formData.membershipCategory === cat}
                            onChange={() => {
                              updateFormData({ membershipCategory: cat });
                            }}
                          />
                          <span className="ml-3 font-medium text-slate-700">{cat}</span>
                        </label>
                      ))}
                    </div>
                    {errors.membershipCategory && <p className="text-red-500 text-xs mt-2 font-medium">{errors.membershipCategory}</p>}
                  </div>
                )}
              </div>
            )}

            {step === 2 && (
              <div className="space-y-6">
                <SectionTitle subtitle={content.steps.personal.subtitle}>{content.steps.personal.title}</SectionTitle>
                {formData.applicationType === ApplicationType.UPGRADING && formData.membershipCategory === MembershipCategory.FELLOW && (
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
                    <FormInput
                      label="Existing Membership ID"
                      value={formData.existingMembershipId}
                      onChange={e => updateFormData({ existingMembershipId: e.target.value })}
                      required
                      error={errors.existingMembershipId}
                      placeholder="Enter your current NIFST Membership ID"
                    />
                    <p className="text-xs text-amber-700 mt-1">You must provide your existing membership ID to apply for Fellow status.</p>
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <FormInput
                    label={content.labels.surname}
                    value={formData.surname}
                    onChange={e => updateFormData({ surname: e.target.value })}
                    required
                    error={errors.surname}
                  />
                  <FormInput
                    label={content.labels.firstName || 'First Name'}
                    value={formData.firstName}
                    onChange={e => updateFormData({ firstName: e.target.value })}
                    required
                    error={errors.firstName}
                  />
                  <FormInput
                    label={content.labels.otherNames}
                    value={formData.otherNames}
                    onChange={e => updateFormData({ otherNames: e.target.value })}
                    required
                    error={errors.otherNames}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormInput
                    type="date"
                    label={content.labels.dob}
                    value={formData.dob}
                    onChange={e => updateFormData({ dob: e.target.value })}
                    required
                    error={errors.dob}
                  />
                  <FormSelect
                    label={content.labels.sex}
                    options={Object.values(Gender).map(g => ({ value: g, label: g }))}
                    value={formData.sex}
                    onChange={e => updateFormData({ sex: e.target.value as Gender })}
                    required
                    error={errors.sex}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormSelect
                    label={content.labels.maritalStatus}
                    options={Object.values(MaritalStatus).map(s => ({ value: s, label: s }))}
                    value={formData.maritalStatus}
                    onChange={e => updateFormData({ maritalStatus: e.target.value as MaritalStatus })}
                    required
                    error={errors.maritalStatus}
                  />
                </div>

                <FileUpload
                  label={content.labels.passportPhoto}
                  accept="image/*"
                  value={formData.passportPhoto}
                  onUpload={v => updateFormData({ passportPhoto: v })}
                  required
                  error={errors.passportPhoto}
                />
              </div>
            )}

            {step === 3 && (
              <div className="space-y-6">
                <SectionTitle subtitle={content.steps.contact.subtitle}>{content.steps.contact.title}</SectionTitle>
                <div className="space-y-4">
                  <FormTextArea
                    label={content.labels.officeAddress}
                    value={formData.officeAddress}
                    onChange={e => updateFormData({ officeAddress: e.target.value })}
                  />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormInput
                      label={content.labels.officeTelephone}
                      value={formData.officeTelephone}
                      onChange={e => updateFormData({ officeTelephone: e.target.value })}
                    />
                    <FormInput
                      label={content.labels.officeEmail}
                      type="email"
                      value={formData.officeEmail}
                      onChange={e => updateFormData({ officeEmail: e.target.value })}
                      required
                      error={errors.officeEmail}
                    />
                  </div>
                </div>

                <div className="space-y-4 border-t pt-4">
                  <FormTextArea
                    label={content.labels.homeAddress}
                    value={formData.homeAddress}
                    onChange={e => updateFormData({ homeAddress: e.target.value })}
                    required
                    error={errors.homeAddress}
                  />
                  <FormTextArea
                    label={content.labels.postalAddress}
                    value={formData.postalAddress}
                    onChange={e => updateFormData({ postalAddress: e.target.value })}
                  />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormInput
                      label={content.labels.mobileTelephone}
                      value={formData.mobileTelephone}
                      onChange={e => updateFormData({ mobileTelephone: e.target.value })}
                    />
                    <FormInput
                      label={content.labels.homeEmail}
                      type="email"
                      value={formData.homeEmail}
                      onChange={e => updateFormData({ homeEmail: e.target.value })}
                      required
                      error={errors.homeEmail}
                    />
                  </div>
                </div>
              </div>
            )}

            {step === 4 && (
              <div className="space-y-6">
                <SectionTitle subtitle={content.steps.academic.subtitle}>{content.steps.academic.title}</SectionTitle>
                <p className="text-sm text-slate-500 mb-4">Please list your degrees and diplomas.</p>

                {formData.degrees.map((degree, index) => (
                  <div key={degree.id} className="bg-slate-50 border border-slate-200 rounded-xl p-5 space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-xs font-bold text-slate-400 uppercase">Degree {index + 1}</span>
                      {formData.degrees.length > 1 && (
                        <button
                          type="button"
                          onClick={() => {
                            const updated = formData.degrees.filter((_, i) => i !== index);
                            updateFormData({ degrees: updated });
                          }}
                          className="text-red-400 hover:text-red-600 text-xs font-medium transition-colors"
                        >
                          <i className="fa-solid fa-trash mr-1"></i>Remove
                        </button>
                      )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                      <FormInput
                        label="Degree/Diploma Name"
                        value={degree.name}
                        placeholder="e.g. B.Sc. Computer Science"
                        onChange={e => {
                          const updated = [...formData.degrees];
                          updated[index] = { ...updated[index], name: e.target.value };
                          updateFormData({ degrees: updated });
                        }}
                      />
                      <FormInput
                        label="Institution"
                        value={degree.institution}
                        placeholder="e.g. Stanford University"
                        onChange={e => {
                          const updated = [...formData.degrees];
                          updated[index] = { ...updated[index], institution: e.target.value };
                          updateFormData({ degrees: updated });
                        }}
                      />
                      <FormInput
                        label="Year Graduated"
                        value={degree.yearGraduated}
                        placeholder={new Date().getFullYear().toString()}
                        onChange={e => {
                          const updated = [...formData.degrees];
                          updated[index] = { ...updated[index], yearGraduated: e.target.value };
                          updateFormData({ degrees: updated });
                        }}
                      />
                      <FileUpload
                        label="Certificate"
                        accept="image/*,.pdf"
                        value={degree.certificate}
                        onUpload={v => {
                          const updated = [...formData.degrees];
                          updated[index] = { ...updated[index], certificate: v || null };
                          updateFormData({ degrees: updated });
                        }}
                      />
                    </div>
                    <p className="text-[10px] text-slate-400">Max upload size: 500KB</p>
                  </div>
                ))}

                <div className="flex justify-center">
                  <button
                    type="button"
                    onClick={() => {
                      const newEntry: AcademicEntry = {
                        id: Date.now().toString(),
                        name: '',
                        institution: '',
                        yearGraduated: '',
                        certificate: null,
                      };
                      updateFormData({ degrees: [...formData.degrees, newEntry] });
                    }}
                    className="flex items-center gap-2 px-6 py-3 bg-emerald-50 text-emerald-700 rounded-full font-bold text-sm hover:bg-emerald-100 border border-emerald-200 transition-all"
                  >
                    <span className="w-6 h-6 bg-emerald-500 text-white rounded-full flex items-center justify-center text-xs">
                      <i className="fa-solid fa-plus"></i>
                    </span>
                    + Add another degree
                  </button>
                </div>
              </div>
            )}

            {step === 5 && (
              <div className="space-y-6">
                <SectionTitle subtitle={content.steps.student.subtitle}>{content.steps.student.title}</SectionTitle>
                {formData.membershipCategory === MembershipCategory.STUDENT ? (
                  <div className="space-y-4">
                    <FormInput
                      label={content.labels.studentCourse}
                      value={formData.studentCourse}
                      onChange={e => updateFormData({ studentCourse: e.target.value })}
                    />
                    <FormInput
                      label={content.labels.studentInstitution}
                      value={formData.studentInstitution}
                      onChange={e => updateFormData({ studentInstitution: e.target.value })}
                    />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormInput
                        type="date"
                        label={content.labels.studentCommenced}
                        value={formData.studentCommenced}
                        onChange={e => updateFormData({ studentCommenced: e.target.value })}
                      />
                      <FormInput
                        type="date"
                        label={content.labels.studentCompletion}
                        value={formData.studentCompletion}
                        onChange={e => updateFormData({ studentCompletion: e.target.value })}
                      />
                    </div>
                    <FormSelect
                      label={content.labels.studentMode}
                      options={[{ value: 'Full-Time', label: 'Full-Time' }, { value: 'Part-Time', label: 'Part-Time' }]}
                      value={formData.studentMode}
                      onChange={e => updateFormData({ studentMode: e.target.value as any })}
                    />

                    <div className="mt-6 pt-6 border-t border-slate-200 space-y-4">
                      <h4 className="font-bold text-slate-700 text-sm">Head of Department (HOD) Endorsement</h4>
                      <p className="text-xs text-slate-500">Upload a letter/certificate from your HOD and their signature to support your student membership application.</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <FileUpload
                          label="HOD Certificate / Letter"
                          accept="image/*,.pdf"
                          value={formData.hodCertificate}
                          onUpload={v => updateFormData({ hodCertificate: v })}
                          error={errors.hodCertificate}
                        />
                        <FileUpload
                          label="HOD Signature"
                          accept="image/*"
                          value={formData.hodSignature}
                          onUpload={v => updateFormData({ hodSignature: v })}
                          error={errors.hodSignature}
                        />
                      </div>
                      <p className="text-[10px] text-slate-400">Max upload size: 500KB per file</p>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500 bg-slate-50 rounded-lg">
                    Not applicable for {formData.membershipCategory}.
                  </div>
                )}
              </div>
            )}

            {step === 6 && (
              <div className="space-y-6">
                <SectionTitle subtitle={content.steps.employment.subtitle}>{content.steps.employment.title}</SectionTitle>
                <FormTextArea
                  label={content.labels.employmentHistory}
                  value={formData.employmentHistory}
                  onChange={e => updateFormData({ employmentHistory: e.target.value })}
                  rows={6}
                />
                <p className="text-xs text-orange-500">Note: Please list your employment history in the box above.</p>
              </div>
            )}

            {step === 7 && (
              <div className="space-y-6">
                <SectionTitle subtitle={content.steps.affiliations.subtitle}>{content.steps.affiliations.title}</SectionTitle>
                <FormTextArea
                  label={content.labels.professionalBodies}
                  value={formData.professionalBodies}
                  onChange={e => updateFormData({ professionalBodies: e.target.value })}
                  rows={4}
                />
              </div>
            )}

            {step === 8 && (
              <div className="space-y-6">
                <SectionTitle subtitle={content.steps.career.subtitle}>{content.steps.career.title}</SectionTitle>
                <div className="relative">
                  <FormTextArea
                    label={content.labels.careerSummary}
                    value={formData.careerSummary}
                    onChange={e => updateFormData({ careerSummary: e.target.value })}
                    rows={8}
                    className="w-full"
                  />
                </div>
              </div>
            )}

            {step === 9 && (
              <div className="space-y-6">
                <SectionTitle subtitle={content.steps.referees.subtitle}>{content.steps.referees.title}</SectionTitle>
                <p className="text-sm text-slate-500">Two financial members of the Institute not below the grade of Member.</p>

                <div className="bg-slate-50 p-4 rounded-lg space-y-3">
                  <h4 className="font-bold text-slate-700 mb-2">Referee 1</h4>
                  <FormInput
                    label={content.labels.referee1Name}
                    value={formData.referee1.name}
                    onChange={e => updateFormData({ referee1: { ...formData.referee1, name: e.target.value } })}
                    required
                    error={errors['referee1.name']}
                  />
                  <FormInput
                    label={content.labels.referee1Id}
                    value={formData.referee1.membershipId}
                    onChange={e => updateFormData({ referee1: { ...formData.referee1, membershipId: e.target.value } })}
                    required
                    error={errors['referee1.membershipId']}
                  />
                </div>

                <div className="bg-slate-50 p-4 rounded-lg space-y-3">
                  <h4 className="font-bold text-slate-700 mb-2">Referee 2</h4>
                  <FormInput
                    label={content.labels.referee2Name}
                    value={formData.referee2.name}
                    onChange={e => updateFormData({ referee2: { ...formData.referee2, name: e.target.value } })}
                    required
                    error={errors['referee2.name']}
                  />
                  <FormInput
                    label={content.labels.referee2Id}
                    value={formData.referee2.membershipId}
                    onChange={e => updateFormData({ referee2: { ...formData.referee2, membershipId: e.target.value } })}
                    required
                    error={errors['referee2.membershipId']}
                  />
                </div>
              </div>
            )}

            {step === 10 && (
              <div className="space-y-6">
                <SectionTitle subtitle={content.steps.chapter.subtitle}>{content.steps.chapter.title}</SectionTitle>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormSelect
                    label={content.labels.chapterName}
                    value={formData.chapterName}
                    onChange={e => updateFormData({ chapterName: e.target.value })}
                    options={[
                      { value: 'Lagos Chapter', label: 'Lagos Chapter' },
                      { value: 'FCT Abuja Chapter', label: 'FCT Abuja Chapter' },
                      { value: 'East Central', label: 'East Central' },
                      { value: 'South East Chapter', label: 'South East Chapter' },
                      { value: 'Edo/Delta Chapter', label: 'Edo/Delta Chapter' },
                      { value: 'Middle Belt chapter', label: 'Middle Belt chapter' },
                      { value: 'North East Chapter', label: 'North East Chapter' },
                      { value: 'North Central Chapter', label: 'North Central Chapter' },
                      { value: 'North West Chapter', label: 'North West Chapter' },
                      { value: 'Western Chapter', label: 'Western Chapter' },
                    ]}
                  />
                  <FormInput
                    label={content.labels.chapterChairman}
                    value={formData.chapterChairman}
                    onChange={e => updateFormData({ chapterChairman: e.target.value })}
                  />
                </div>
              </div>
            )}

            {step === 13 && (
              <div className="space-y-6">
                <SectionTitle subtitle={content.steps.fellowUploads?.subtitle}>{content.steps.fellowUploads?.title || 'Fellow Application'}</SectionTitle>
                
                <div className="bg-amber-50 p-4 rounded-xl border border-amber-200 mb-6">
                  <p className="text-sm text-amber-800">Please provide the following documents to support your application for Fellow.</p>
                </div>

                <FileUpload
                  label={content.labels.fellowCitation || 'Citation (maximum 500 words)'}
                  accept=".pdf,.doc,.docx"
                  value={formData.fellowCitationUpload}
                  onUpload={v => updateFormData({ fellowCitationUpload: v })}
                  required
                  error={errors.fellowCitationUpload}
                />
                <FileUpload
                  label={content.labels.fellowStatement || 'Statement of Activities (maximum 100 words)'}
                  accept=".pdf,.doc,.docx"
                  value={formData.fellowStatementUpload}
                  onUpload={v => updateFormData({ fellowStatementUpload: v })}
                  required
                  error={errors.fellowStatementUpload}
                />
              </div>
            )}

            {step === 11 && (
              <div className="space-y-8">
                <SectionTitle>{content.steps.declaration.title}</SectionTitle>
                <div className="bg-emerald-50 p-8 rounded-xl border-l-4 border-[var(--primary-color)]">
                  <label className="flex items-center gap-3 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      className="w-6 h-6 rounded text-[var(--primary-color)]"
                      checked={formData.declarationConfirmed}
                      onChange={e => updateFormData({ declarationConfirmed: e.target.checked })}
                    />
                    <span className="text-slate-700 font-bold">{declarationText} <span className="text-red-500">*</span></span>
                  </label>
                </div>
                <FileUpload
                  label={content.labels.applicantSignature}
                  required
                  onUpload={sig => updateFormData({ applicantSignature: sig })}
                  value={formData.applicantSignature}
                  accept="image/*"
                  error={errors.applicantSignature}
                />
              </div>
            )}

            {step === 12 && !isAdmin && (
              <PaymentStep
                formConfig={formConfig}
                category={formData.membershipCategory}
                email={formData.officeEmail}
                name={`${formData.surname} ${formData.otherNames}`}
                phone={formData.mobileTelephone}
                isSubmitting={isSubmitting}
                onPaymentComplete={handlePaymentComplete}
                content={content.steps.payment || { title: 'Application Fee', subtitle: 'Pay your application processing fee' }}
              />
            )}
          </form>
        </Card>

        {/* Hide nav bar on payment step for non-admin (PaymentStep has its own buttons) */}
        {!(step === 12 && !isAdmin) && (
          <div className="flex justify-between items-center bg-white p-4 rounded-xl shadow-md border sticky bottom-4 z-40">
            <button onClick={handleBack} disabled={step === 1} className="px-6 py-2 rounded-lg font-bold text-slate-600 hover:bg-slate-50 disabled:opacity-30 transition-all">
              <i className="fa-solid fa-arrow-left"></i> Previous
            </button>

            <div className="flex items-center gap-4">
              {activeSteps.indexOf(step) < activeSteps.length - 1 && !(activeSteps.indexOf(step) === activeSteps.length - 2 && !isAdmin) ? (
                <button type="button" onClick={handleNext} className="flex items-center gap-2 px-8 py-2 bg-[var(--primary-color)] text-white rounded-lg font-bold hover:opacity-90 shadow-lg transition-all">
                  Next Step <i className="fa-solid fa-arrow-right"></i>
                </button>
              ) : activeSteps.indexOf(step) === activeSteps.length - 2 && !isAdmin ? (
                <button
                  type="button"
                  onClick={() => {
                    const stepErrors = validateStep(step);
                    if (Object.keys(stepErrors).length > 0) {
                      setErrors(stepErrors);
                      return;
                    }
                    setErrors({});
                    setStep(12);
                    window.scrollTo(0, 0);
                  }}
                  className="flex items-center gap-2 px-8 py-2 bg-[var(--primary-color)] text-white rounded-lg font-bold hover:opacity-90 shadow-lg transition-all"
                >
                  Proceed to Payment <i className="fa-solid fa-arrow-right"></i>
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="flex items-center gap-2 px-8 py-2 bg-[var(--secondary-color)] text-white rounded-lg font-bold hover:opacity-90 shadow-lg transition-all"
                >
                  {isSubmitting ? <i className="fa-solid fa-spinner fa-spin"></i> : <i className="fa-solid fa-paper-plane"></i>}
                  {isSubmitting ? 'Updating...' : 'Update Member Data'}
                </button>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default RegistrationWizard;
